import { Component, EventEmitter, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef }
	from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import {
	GridApi, GridOptions, GridReadyEvent, ICellRendererParams,
	Module, ViewportChangedEvent
} from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatButtonModule } from '@angular/material/button';

import { DplScope, IGNORED_VARIABLES, OrderHistory } from '../../../models/need4steel';
import { CheckboxColDef, CommonColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { ColumnType } from '../../../../shared/datacenter/column-type';
import { DatacenterTextFilterComponent } from
	'../../../../shared/datacenter/filter-type/datacenter-text-filter.component';
import { serverSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { TranslationService } from '../../../../shared/services/translation.service';
import { CodeValue } from '../../../../shared/models/code-value';
import { Need4SteelService } from '../../../services/need4steel.api.service';
import { Need4SteelOrderHistoryDataSource } from '../../../services/need4steel-order-history-datasource';
import { NAMESPACE_A2C_FIELD, ProductCharacteristicsTranslationService } from
	'../../../../shared/services/product-characteristics-translation.services';

@Component({
	selector: 'copo-need4steel-order-history',
	templateUrl: './need4steel-order-history.component.html',
	styleUrl: './need4steel-order-history.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		MatIconModule,
		NgIf,
		AgGridModule,
		MatDialogModule,
		MatButtonModule
	]
})
export class DialogNeed4SteelOrderHistoryComponent implements OnDestroy {
	gridOptions: GridOptions;
	showNoRowsOverlaySub?: Subscription;
	modules: Module[] = serverSideAgGridModules;
	gridApi?: GridApi<Map<string, object>[]>;
	showNoOrderMessage = false;
	dataGridReady = new EventEmitter<GridApi>();
	private need4steelHistoryDatasource?: Need4SteelOrderHistoryDataSource;
	constructor(
		@Inject(MAT_DIALOG_DATA) private readonly data: {
			offerRequest: DplScope;
		},
		private readonly dialogRef: MatDialogRef<DialogNeed4SteelOrderHistoryComponent>,
		private readonly translationService: TranslationService,
		private readonly need4SteelService: Need4SteelService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {
		this.gridOptions = this.getGridOptions();
	}

	ngOnDestroy(): void {
		if (this.showNoRowsOverlaySub !== undefined) {
			this.showNoRowsOverlaySub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent<Map<string, object>[]>) {
		this.gridApi = params.api;
		this.need4steelHistoryDatasource = new Need4SteelOrderHistoryDataSource(
			this.need4SteelService,
			params.api,
			this.data.offerRequest,
		);
		params.api.setGridOption('serverSideDatasource', this.need4steelHistoryDatasource);
		params.api.sizeColumnsToFit();
		this.showNoRowsOverlaySub = this.need4steelHistoryDatasource.subscribeToRowsCount({
			next: value => {
				if (value) {
					this.showNoOrderMessage = true;
					params.api.showNoRowsOverlay();
				} else {
					params.api.hideOverlay();
				}
			}
		});
		this.dataGridReady.emit(this.gridApi);
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	getGridOptions() {
		return new CommonGridOptions<OrderHistory>({
			alignedGrids: [],
			translationService: this.translationService,
			rowModelType: 'serverSide',
			multiSortKey: 'ctrl',
			cacheBlockSize: 0,
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			columnDefs: [
				new CheckboxColDef<OrderHistory>({
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: true, onDataGridReady: this.dataGridReady },
					editable: true,
					checkboxSelection: true,
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<unknown, unknown>) => Boolean(params.value),
					},
					sortable: false,
					maxWidth: 35,
				}),
				new CommonColDef<OrderHistory>({
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('eorder.product.lastused'),
					isMandatoryOnLeft: false,
					sortable: false,
					field: 'lastUsed',
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					cellRendererParams: DatacenterTextFilterComponent,
					initialSortIndex: 1,
					minWidth: 100
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('issue.batch.steelgrade'),
					columnType: ColumnType.TEXT_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					field: 'steelGrade',
					suppressHeaderMenuButton: true,
					sortable: false,
					cellRendererParams: DatacenterTextFilterComponent,
					valueGetter: params => params.data?.steelGrade.value,
					minWidth: 200,
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('need4steel.wizard.stepper.product.thickness'),
					headerComponent: CheckboxHeaderComponent,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'thicknessValue',
					suppressHeaderMenuButton: true,
					sortable: false,
					cellRendererParams: DatacenterTextFilterComponent,
					minWidth: 75
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('need4steel.wizard.stepper.product.width'),
					headerComponent: CheckboxHeaderComponent,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'widthValue',
					sortable: false,
					suppressHeaderMenuButton: true,
					cellRendererParams: DatacenterTextFilterComponent,
					minWidth: 65
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('need4steel.product.details.product.coilweight'),
					headerComponent: CheckboxHeaderComponent,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'maxNetWeightValue',
					sortable: false,
					suppressHeaderMenuButton: true,
					cellRendererParams: DatacenterTextFilterComponent,
					minWidth: 90
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('need4steel.wizard.thickness.tolerance.field'),
					headerComponent: CheckboxHeaderComponent,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'thicknessTolerance',
					autoHeight: true,
					suppressHeaderMenuButton: true,
					sortable: false,
					valueGetter: params => params.data?.thicknessTolerance.value,
					cellRendererParams: DatacenterTextFilterComponent,
					minWidth: 218
				}),
				new CommonColDef<OrderHistory>({
					headerName: this.translationService.getTR('order.coating'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: true,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => params.data?.coating ? params.data.coating.value : '',
					field: 'coating',
					cellRendererParams: DatacenterTextFilterComponent,
					minWidth: 135
				}),
				new IconColDef<OrderHistory, OrderHistory>({
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('need4steel-product-offers.additional-fields'),
					cellStyle: {
						display: 'flex',
						// eslint-disable-next-line @typescript-eslint/naming-convention
						'justify-content': 'center',
						// eslint-disable-next-line @typescript-eslint/naming-convention
						'align-items': 'center'
					},
					cellRendererParams: {
						icon(params: ICellRendererParams<OrderHistory, unknown>) {
							if (!params.data) {
								return undefined;
							}
							return {
								className: 'copo-offer-blue-icon',
								iconName: 'help_icon_grey',
							};
						},
						onClick() {
							// noop
						},
					},
					tooltipValueGetter: params => {
						const item = params.data;
						const additionalFields = [];
						if (item) {
							for (const key of Object.keys(item)) {
								if (!IGNORED_VARIABLES.has(key)) {
									const data = item[key];
									const additionalfieldKey =	this.getFieldLabel(key);
									if (data && typeof data === 'object') {
										const additionalData = <CodeValue><unknown>data;
										additionalFields.push(`${ additionalfieldKey }: ${ additionalData.value }`);
									} else {
										additionalFields.push(`${ additionalfieldKey }: ${ data }`);
									}
								}
							}
						}
						return additionalFields.join(',\n');
					},
					maxWidth: 80,
					width: 80
				})
			],
			headerHeight: 50
		});
	}

	getFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_A2C_FIELD }${ fieldLabel }`);
	}

	importHistory() {
		this.dialogRef.close(this.gridApi?.getSelectedRows());
	}
}
